import type { TFunction } from "i18next";
import { getCurrentRegion } from "@/utils/environment";
import { isCraveUser } from "@/utils/permissionManager";

interface StoreSelectorLabelSource {
  id: number | string;
  name: string | null | undefined;
  externalId?: string | null | undefined;
  customerId?: number | null | undefined;
  customerInitial?: string | null | undefined;
}

// TEMPORARY_CUSTOMER_STORE_LABEL_REFORMATTING: Keep this switch only for a
// rollback while the store-title migration is in progress.
const disableCustomerStoreLabelReformatting = false;

// TEMPORARY_LEGACY_STORE_TITLE_INITIALS_BY_CUSTOMER_ID: Remove entries as
// customer store titles are migrated. These are extra legacy prefixes beyond
// the customer's current initial.
const legacyStoreTitleInitialsByCustomerId: Readonly<Record<number, readonly string[]>> = {
  4: ["P"],
  7: ["SL"],
  8: ["VSFA"],
  18: ["VSEU"],
  19: ["GL"],
  21: ["A&F"],
  24: ["LE"],
  39: ["A&F"],
};

export type LegacyCustomerStoreLabelStyle = "id-name" | "name" | "id-em-dash-name";

export const shouldUseLegacyCustomerStoreLabel = (userRole: string | undefined): boolean =>
  !isCraveUser(userRole ?? "") && disableCustomerStoreLabelReformatting;

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const normalizeExternalIdForComparison = (externalId: string) =>
  externalId.replace(/^0+(?=\d)/, "");

const nonStandardExternalIdHelper = {
  isExternalId: (externalId: string | null | undefined) => {
    const trimmedExternalId = externalId?.trim();

    if (!trimmedExternalId) return false;

    return (
      trimmedExternalId.toLowerCase().startsWith("gid://") ||
      /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
        trimmedExternalId,
      )
    );
  },
  reformatTitle: (storeName: string | null | undefined): string => {
    const trimmedName = storeName?.trim() ?? "";
    const matches = [...trimmedName.matchAll(/\(([^()]+)\)/g)];
    const idMatch = matches.at(-1);
    const titleId = idMatch?.[1]?.trim();

    if (!titleId || !idMatch) return trimmedName;

    const title = trimmedName
      .slice(0, idMatch.index)
      .concat(trimmedName.slice((idMatch.index ?? 0) + idMatch[0].length))
      .replace(/\s{2,}/g, " ")
      .replace(/\s+([,.;:!?])/g, "$1")
      .trim();

    return title ? `${titleId} - ${title}` : titleId;
  },
};

const getDisplayExternalId = (externalId: string | null | undefined) => {
  const trimmedExternalId = externalId?.trim();

  return trimmedExternalId && !nonStandardExternalIdHelper.isExternalId(trimmedExternalId)
    ? trimmedExternalId
    : undefined;
};

const normalizeStoreTitle = (storeTitle: string): string =>
  storeTitle
    .replace(/\s{2,}/g, " ")
    .replace(/\s+([,.;:!?])/g, "$1")
    .replace(/(?:\s*-\s*){2,}/g, " - ")
    .replace(/^\s*-\s*|\s*-\s*$/g, "")
    .trim();

/**
 * Removes redundant customer/region prefixes from legacy store titles while
 * the source records are being migrated to title-only values.
 * Customer initials occupy the first word; the optional region is the second.
 */
export const stripLegacyStoreTitlePrefixes = (
  storeTitle: string | null | undefined,
  customerInitial: string | null | undefined,
  customerId?: number | null,
): string => {
  const legacyInitials = customerId ? (legacyStoreTitleInitialsByCustomerId[customerId] ?? []) : [];
  // Empty customer initials leave the first word unchanged unless a configured legacy initial matches.
  const initials = [customerInitial ?? "", ...legacyInitials].map((initial) =>
    initial.trim().toLowerCase(),
  );
  const words = (storeTitle?.trim() ?? "").split(/\s+/);
  const region = getCurrentRegion().toLowerCase();

  return normalizeStoreTitle(
    words
      .filter((word, index) => {
        if (index === 0) return !initials.includes(word.toLowerCase());
        if (index === 1) return word.toLowerCase() !== region;
        return true;
      })
      .join(" "),
  );
};

export const stripExternalIdFromStoreName = (
  storeName: string | null | undefined,
  externalId: string | null | undefined,
): string => {
  const trimmedName = storeName?.trim() ?? "";
  const trimmedExternalId = externalId?.trim();

  if (!trimmedName || !trimmedExternalId) return trimmedName;

  const firstExternalIdComponent = trimmedExternalId.includes("-")
    ? trimmedExternalId.split("-", 1)[0]?.trim()
    : undefined;
  const comparisonIds = [
    trimmedExternalId,
    normalizeExternalIdForComparison(trimmedExternalId),
    ...(firstExternalIdComponent
      ? [firstExternalIdComponent, normalizeExternalIdForComparison(firstExternalIdComponent)]
      : []),
  ];
  const externalIdPatterns = [...new Set(comparisonIds)].flatMap((comparisonId) => {
    const normalizedId = normalizeExternalIdForComparison(comparisonId);

    return /^\d+$/.test(comparisonId)
      ? [escapeRegExp(comparisonId), `0*${escapeRegExp(normalizedId)}`]
      : [escapeRegExp(comparisonId)];
  });
  const externalIdPattern = [...new Set(externalIdPatterns)].join("|");
  const parenthesizedId = new RegExp(`\\s*\\((?:${externalIdPattern})\\)`, "gi");
  const delimitedId = new RegExp(`(?:\\s+|-\\s*)(?:${externalIdPattern})(?=$|\\s|-)`, "gi");

  return trimmedName
    .replace(parenthesizedId, " ")
    .replace(delimitedId, " ")
    .replace(/\s{2,}/g, " ")
    .replace(/\s+([,.;:!?])/g, "$1")
    .trim();
};

export const formatStoreSelectorLabel = (
  store: StoreSelectorLabelSource,
  t: TFunction,
  userRole: string | undefined,
  legacyCustomerLabelStyle?: LegacyCustomerStoreLabelStyle,
): string => {
  const externalId = getDisplayExternalId(store.externalId);
  const isCraveAccount = isCraveUser(userRole ?? "");

  if (shouldUseLegacyCustomerStoreLabel(userRole) && legacyCustomerLabelStyle) {
    const name = store.name?.trim() ?? "";

    switch (legacyCustomerLabelStyle) {
      case "name":
        return name;
      case "id-em-dash-name":
        return `${store.id} — ${name}`;
      case "id-name":
        return `${store.id} - ${name}`;
    }
  }

  const isNonStandardExternalId = nonStandardExternalIdHelper.isExternalId(store.externalId);
  const useCustomerLabelReformatting = !isCraveAccount;
  const title = stripLegacyStoreTitlePrefixes(store.name, store.customerInitial, store.customerId);
  const name = isNonStandardExternalId
    ? useCustomerLabelReformatting
      ? nonStandardExternalIdHelper.reformatTitle(title)
      : title
    : stripExternalIdFromStoreName(title, externalId);
  const internalName = [store.customerInitial?.trim(), getCurrentRegion(), name]
    .filter(Boolean)
    .join(" ");

  return t(
    externalId
      ? useCustomerLabelReformatting
        ? "common.storeSelector.label.externalIdFirst"
        : "common.storeSelector.label.withExternalId"
      : useCustomerLabelReformatting
        ? "common.storeSelector.label.nameFirst"
        : "commandCenter.filters.storeOption",
    {
      id: store.id,
      name: useCustomerLabelReformatting ? name : internalName,
      ...(externalId ? { externalId } : {}),
    },
  );
};
